import { VestingTemplate } from "./VestingTemplate";
import { OnExpiryAction } from "./GrantApproval";
import { DateFormatKey } from "../utils/dateFormatCatalog";

// ─── New Forfeiture / Expiry types (matches backend plan.ts) ─────────────────

export enum ExpiryMode {
  GRANT_EXPIRY = "GRANT_EXPIRY",
  FROM_VESTING_DATE = "FROM_VESTING_DATE",
  NEVER = "NEVER",
  SPECIFIC_DATE = "SPECIFIC_DATE",
}

export enum EmployeeExitType {
  RESIGNATION = "RESIGNATION",
  TERMINATION = "TERMINATION",
  RETIREMENT = "RETIREMENT",
  PERFORMANCE_RELATED = "PERFORMANCE_RELATED",
  MISCONDUCT = "MISCONDUCT",
  PERMANENT_DISABILITY = "PERMANENT_DISABILITY",
  DEATH = "DEATH",
  ABANDONMENT = "ABANDONMENT",
}

export enum ExerciseWindowUnit {
  DAYS = "DAYS",
  MONTHS = "MONTHS",
  YEARS = "YEARS",
}

export interface ExerciseWindow {
  value: number;
  unit: ExerciseWindowUnit;
  neverExpires?: boolean;
}

export interface ExitForfeitureRule {
  exitType: EmployeeExitType;
  conditionalResolution?: "EARLIER_DATE" | "LATER_DATE";
  vestedOptions: {
    exerciseWindow: ExerciseWindow;
    onExpiry: "FORFEIT" | "LAPSE";
  };
  unvestedOptions: {
    forfeitImmediately: boolean;
    gracePeriodDays?: number;
    acceleration?: {
      enabled: boolean;
      percentage: number;
    };
  };
  conditionalRules?: Array<{
    tenure?: {
      minMonths: number;
      maxMonths?: number;
      /** Unit for minMonths/maxMonths values. Defaults to MONTHS for backward compat. */
      unit?: ExerciseWindowUnit;
    };
    vestedOptions?: {
      exerciseWindow: ExerciseWindow;
      onExpiry: "FORFEIT" | "LAPSE";
    };
    unvestedOptions?: {
      forfeitImmediately: boolean;
      gracePeriodDays?: number;
      acceleration?: {
        enabled: boolean;
        percentage: number;
      };
    };
  }>;
}

export interface ActiveEmployeeExpiry {
  expiryMode: ExpiryMode;
  expiryValue?: number;
  expiryUnit?: ExerciseWindowUnit;
  /** ISO-8601 date string; only used when expiryMode = SPECIFIC_DATE */
  specificDate?: string;
}

export enum ExitDateBasis {
  LAST_WORKING_DATE = "LAST_WORKING_DATE",
  RESIGNATION_DATE = "RESIGNATION_DATE",
}

/**
 * Optional clamp applied on top of the resolved exit exercise-window expiry:
 * compares it against the *other* exit date (raw, no offset) and picks the
 * earlier/later of the two.
 */
export interface DualDateGuard {
  enabled: boolean;
  resolution: "EARLIER_DATE" | "LATER_DATE";
}

export interface ForfeitureSettings {
  activeEmployee: ActiveEmployeeExpiry;
  exitRules: ExitForfeitureRule[];
  exitDateBasis?: ExitDateBasis;
  dualDateGuard?: DualDateGuard;
}

export type ApprovalOrder = "SEQUENTIAL" | "NO_ORDER";

export interface GrantApproverEntry {
  userId: string;
  name: string;
  email: string;
  role: string;
  order: number;
  expiryDays?: number;
  onExpiryAction?: OnExpiryAction;
}

export interface GrantApprovalConfig {
  requireSignOff: boolean;
  approvalOrder: ApprovalOrder;
  approvers: GrantApproverEntry[];
  minApprovers?: number;
}

export type EsopPlan = {
  scope: string;
  esopPlanId: string;
  planType: string;
  planHoldingPercentage: number;
  planName: string;
  planDescription: string;
  totalShares: number;
  issuedShares: number;
  esopPlanState: PlanState;
  optionsReserved: number;
  planStartDate: string;
  poolSize: number;
  conversionNumber: number;
  conversionShares: number;
  isFractional: boolean;
  trustId: string;
  sharesManagedByTrust: number;
  availableState: PlanState[];
  isStateChangeAllowed: boolean;
  unitName: string;
  unitType: string;
  note?: string;
  strikePriceType?: string;
  outstandingPool: number;
  outstandingAllocatedPool: number;
  stockExercised: number;
  returnedBackToPool: number;
  optionsVested: number;
  outstandingVested: number;
  cashedOutNotReturnedToPool: number;
  roundingSettings?: RoundingSettings | null;
  grantApprovalConfig?: GrantApprovalConfig;
};
export interface FinancialReportReq {
  showFutureVestings: boolean;
  date: string;
  includeResignedEmployees: boolean;
  planIds: string[];
  dateFormat?: DateFormatKey;
}

export interface AddPlanRes {
  id: string;
  planName: string;
  planType: string;
  planDescription: string;
  poolSize: number;
  planTerm: number;
  planStartDate?: string;
  note?: string;
  esopPlanState: PlanState;
  conversionNumber: number;
  conversionShares: number;
  vesting: VestingTemplate;
  isFractional: boolean;
  isManagedByTrust: boolean;
  trustId: string;
  trustName: string;
  sharesManagedByTrust: number;
  unitType: string;
  unitName: string;
  forfeitureSettings?: ForfeitureSettings;
  strikePriceType: StrikePriceType;
  noCashedOutPoolReturn?: boolean;
  roundingSettings?: RoundingSettings | null;
  grantApprovalConfig?: GrantApprovalConfig;
}

export interface AddPlanReq {
  id?: string;
  scope?: string;
  isFractional: boolean;
  isManagedByTrust: boolean;
  trustId?: string;
  trustName: string;
  sharesManagedByTrust: number;
  planTerm: number;
  planType: string;
  vestingTemplateId: string;
  vestingTemplateName: string | null;
  planName: string;
  planDescription: string;
  planStartDate?: string;
  poolSize: number;
  notes: string;
  conversionNumber: number;
  conversionShares: number;
  poolShares: number;
  unitType: string;
  unitName: string;
  forfeitureSettings: ForfeitureSettings;
  strikePriceType: StrikePriceType;
  noCashedOutPoolReturn: boolean;
  roundingSettings?: RoundingSettings | null;
  grantApprovalConfig?: GrantApprovalConfig;
}

export enum StrikePriceType {
  OPTIONS_PRICE = "OPTIONS_PRICE",
  SHARE_PRICE = "SHARE_PRICE",
}

export enum RoundingStrategy {
  ROUND_UP = "ROUND_UP",
  ROUND_DOWN = "ROUND_DOWN",
  ROUND_OFF = "ROUND_OFF",
}

export interface RoundingSettings {
  strategy: RoundingStrategy;
  multiple: number;
}

export interface SH6ReportReq {
  planIds: string[];
  date: string;
  maskPiiData: boolean;
  grantStates: string[];
  selectedColumns?: string[];
  customHeaders?: Record<string, string>;
  dateFormat?: DateFormatKey;
}

export interface GenericAxiosResponse {
  response?: {
    data?: {
      resultCode: string;
      reason: string;
    };
  };
}

export interface EsopAccountingReq {
  attritionValue: number;
  hasSelectedIGAAP?: boolean;
  hasSelectedFairMarketValue: boolean;
  hasSelectedGradedMethod: boolean;
  changeValuationPerVesting: boolean;
  startDate: Date;
  endDate: Date;
  planType: string;
  planIds?: string[];
  shouldPiiBeMasked: boolean;
  grantStates?: string[];
  employmentType?: string[];
  departments?: string[];
  grantIds?: string[];
  employeeIds?: string[];
  companyIds?: string[];
  customValuationExcelFile?: string;
  isFileUploaded: boolean;
}

export enum PlanState {
  ACTIVE = "ACTIVE",
  FOR_APPROVAL = "FOR_APPROVAL",
  AMENDMENT = "AMENDMENT",
  REJECTED = "REJECTED",
  CONSTRUCTED = "CONSTRUCTED",
}
